/** @type {Promise<any> | undefined} */
let packeryLoader;

const SESSION_PREFIX = "vague-packery:";
const FRACTION_KEYS = ["packeryDesktopFraction", "packeryMobileFraction"];
const SCROLL_RESTORE_KEY = "vague-packery-scroll";
const SCROLL_RESTORE_PENDING = "pending";

/**
 * Prefetch the next page once the sentinel is this close to the viewport.
 * The storefront renders the first 30 products in the HTML; everything after
 * that arrives through this observer.
 */
const LOAD_MORE_ROOT_MARGIN = "800px 0px";

/**
 * @param {Function} fn
 * @param {number} wait
 * @returns {Function}
 */
function debounce(fn, wait) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), wait);
  };
}

/**
 * Loads the vendored Packery bundle once and resolves with the Packery constructor.
 *
 * @param {string} src - Asset URL of packery.pkgd.min.js
 * @returns {Promise<any>}
 */
function loadPackery(src) {
  const existing = /** @type {any} */ (window).Packery;
  if (existing) return Promise.resolve(existing);

  packeryLoader ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      const Packery = /** @type {any} */ (window).Packery;
      if (typeof Packery !== "function") {
        reject(new Error("Packery global was not defined after script load"));
        return;
      }
      resolve(Packery);
    };
    script.onerror = () =>
      reject(new Error(`Failed to load Packery from ${src}`));
    document.head.appendChild(script);
  });

  return packeryLoader;
}

/**
 * Resolves once the element has a non-zero layout width.
 * Custom elements default to display:inline; if section CSS is late, clientWidth
 * can be 0 on connect and Packery would initialize incorrectly.
 *
 * @param {HTMLElement} element
 * @returns {Promise<number>}
 */
function whenHasWidth(element) {
  if (element.clientWidth > 0) return Promise.resolve(element.clientWidth);

  return new Promise((resolve) => {
    const observer = new ResizeObserver(() => {
      if (element.clientWidth > 0) {
        observer.disconnect();
        resolve(element.clientWidth);
      }
    });
    observer.observe(element);

    // Fallback if the element never reports a width (e.g. display:none).
    setTimeout(() => {
      observer.disconnect();
      resolve(element.clientWidth);
    }, 2000);
  });
}

/**
 * @param {string} sessionKey
 * @returns {string}
 */
function sessionStorageKey(sessionKey) {
  return `${SESSION_PREFIX}${sessionKey || "gallery"}`;
}

/**
 * @param {string} sessionKey
 * @returns {Record<string, Record<string, string>>}
 */
function readSessionFractions(sessionKey) {
  try {
    const raw = sessionStorage.getItem(sessionStorageKey(sessionKey));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * @param {string} sessionKey
 * @param {Record<string, Record<string, string>>} fractions
 */
function writeSessionFractions(sessionKey, fractions) {
  try {
    sessionStorage.setItem(
      sessionStorageKey(sessionKey),
      JSON.stringify(fractions),
    );
  } catch {
    // Private mode or full storage should not break the grid.
  }
}

/**
 * @param {HTMLElement} item
 * @returns {string}
 */
function itemSessionId(item) {
  return item.dataset.packeryKey || "";
}

/**
 * Scroll restoration for the lazy-loaded product grid.
 *
 * The homepage and category pages render the first 30 products in the HTML
 * (`packery_page_size` in snippets/product-gallery.liquid). Further pages are
 * fetched when the sentinel is within LOAD_MORE_ROOT_MARGIN of the viewport.
 * Those later products are not in the document when the browser restores
 * scroll, so an offset past the first page clamps to the bottom of that page
 * and stays there after the rest of the grid arrives.
 *
 * Leaving. A product click, with pagehide as a backup, writes a
 * ScrollRestoreState record to sessionStorage. needsRestore is true only when
 * a later page was fetched and the offset sits below the first page. Offsets
 * that still fit on the first page keep needsRestore false, and the browser
 * restores them on its own.
 *
 * Coming back. The inline script in layout/theme.liquid runs in the head,
 * before first paint. On a back or forward navigation whose record has
 * needsRestore, it sets history.scrollRestoration to "manual", stretches the
 * document so the saved offset fits, and sets
 * data-packery-scroll-restore="pending". Critical CSS hides the grid while
 * that attribute is set. A script at the top of body jumps to the offset
 * immediately, so the first paint is not the top of the page.
 * installEarlyPackeryScrollRestore() repeats that check when this file
 * evaluates. Whichever of the two runs first sets the pending mark.
 * PackeryLayout then pauses the load-more observer, fetches pages until the
 * saved item count is in the DOM, scrolls to the offset, reveals the grid,
 * and turns the observer back on. After the load event it hands
 * scrollRestoration back to the browser.
 *
 * A pageshow from the back/forward cache only re-applies the offset, because
 * the products are already in the DOM. A wheel, touch, or arrow key during
 * the fetch cancels the correction. A click while a restore is still pending
 * does not overwrite the saved record. Some browsers zero scrollY before
 * pagehide; the click handler has already stored the real offset. If the
 * fetch never finishes, the head script clears the pending mark after 12
 * seconds so the grid cannot stay hidden.
 *
 * @typedef {Object} ScrollRestoreState
 * @property {string} href pathname + search of the grid page
 * @property {string} sessionKey data-session-key on packery-layout
 * @property {number} scrollX
 * @property {number} scrollY
 * @property {number} itemCount [data-packery-item] nodes present when the shopper left
 * @property {boolean} needsRestore true only past the first server-rendered page
 */

/**
 * @returns {string}
 */
function pageHref() {
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * @returns {boolean}
 */
function isBackForwardNavigation() {
  const nav = performance.getEntriesByType("navigation")[0];
  return nav?.type === "back_forward";
}

/**
 * Document height the first server-rendered page would have had, including
 * the chrome below the grid (spacer, footer). Later pages sit between the
 * first page's items and that chrome, so the first page's height is the
 * first items' bottom plus whatever still trails the lowest item.
 *
 * @param {{ initialBottoms: number[], allBottoms: number[], scrollHeight: number }} measurements
 * @returns {number}
 */
function estimateInitialScrollHeight({
  initialBottoms,
  allBottoms,
  scrollHeight,
}) {
  const initialBottom = initialBottoms.length ? Math.max(...initialBottoms) : 0;
  const allBottom = allBottoms.length
    ? Math.max(initialBottom, ...allBottoms)
    : initialBottom;
  const trailing = Math.max(0, scrollHeight - allBottom);
  return initialBottom + trailing;
}

/**
 * Native scroll restoration only lands when the saved offset still fits in
 * the server-rendered page. Once the shopper has scrolled into a lazy-loaded
 * page, the browser clamps to the bottom of the first page and stays there
 * after the rest of the grid arrives.
 *
 * @param {{ scrollY: number, viewportHeight: number, initialScrollHeight: number, loadedExtra: boolean }} state
 * @returns {boolean}
 */
function needsPackeryScrollRestore({
  scrollY,
  viewportHeight,
  initialScrollHeight,
  loadedExtra,
}) {
  if (!loadedExtra) return false;
  if (!(scrollY > 0) || !(initialScrollHeight > 0)) return false;
  const viewport = viewportHeight > 0 ? viewportHeight : 0;
  const initialMaxScroll = Math.max(0, initialScrollHeight - viewport);
  return scrollY > initialMaxScroll + 1;
}

/**
 * @returns {Record<string, any> | null}
 */
function readScrollRestore() {
  try {
    const raw = sessionStorage.getItem(SCROLL_RESTORE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * @param {Record<string, any>} state
 */
function writeScrollRestore(state) {
  try {
    sessionStorage.setItem(SCROLL_RESTORE_KEY, JSON.stringify(state));
  } catch {
    // Private mode or full storage should not break the grid.
  }
}

/**
 * @returns {Record<string, any> | null}
 */
function matchingScrollRestore() {
  const saved = readScrollRestore();
  if (!saved?.needsRestore) return null;
  if (saved.href !== pageHref()) return null;
  return saved;
}

/**
 * Head-script fallback. See the scroll restoration note above pageHref().
 * Keep this in sync with the inline script in layout/theme.liquid.
 */
function installEarlyPackeryScrollRestore() {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const root = document.documentElement;
  const already = root.getAttribute("data-packery-scroll-restore") === SCROLL_RESTORE_PENDING;

  if (!already) {
    if (!isBackForwardNavigation()) return;
    const saved = matchingScrollRestore();
    if (!saved) return;

    const y = Number(saved.scrollY) || 0;
    const x = Number(saved.scrollX) || 0;
    root.setAttribute("data-packery-scroll-restore", SCROLL_RESTORE_PENDING);
    root.setAttribute("data-packery-scroll-x", String(x));
    root.setAttribute("data-packery-scroll-y", String(y));
    if (!root.hasAttribute("data-packery-scroll-restoration")) {
      root.setAttribute(
        "data-packery-scroll-restoration",
        "scrollRestoration" in history ? history.scrollRestoration : "auto",
      );
    }
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
    root.style.overflowAnchor = "none";
    if (y > 0) {
      root.style.minHeight = `${Math.ceil(y + (window.innerHeight || 0))}px`;
    }
    window.setTimeout(() => {
      if (root.getAttribute("data-packery-scroll-restore") !== SCROLL_RESTORE_PENDING) {
        return;
      }
      root.style.minHeight = "";
      root.style.overflowAnchor = "";
      root.removeAttribute("data-packery-scroll-restore");
    }, 12000);
  }

  // Re-assert after other head scripts. A listener registered on DOMContentLoaded
  // from content_for_header can turn restoration back to auto before load.
  document.addEventListener("DOMContentLoaded", () => {
    if (root.getAttribute("data-packery-scroll-restore") !== SCROLL_RESTORE_PENDING) {
      return;
    }
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
    const y = Number(root.getAttribute("data-packery-scroll-y")) || 0;
    const x = Number(root.getAttribute("data-packery-scroll-x")) || 0;
    if (x || y) window.scrollTo(x, y);
  });

  const y = Number(root.getAttribute("data-packery-scroll-y")) || 0;
  const x = Number(root.getAttribute("data-packery-scroll-x")) || 0;
  if ((x || y) && document.body) window.scrollTo(x, y);
}

/**
 * @param {string} html
 * @returns {{ items: HTMLElement[], nextUrl: string }}
 */
function parsePackeryPage(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const items = [...doc.querySelectorAll("[data-packery-item]")].map((item) =>
    /** @type {HTMLElement} */ (document.importNode(item, true)),
  );
  const sentinel = doc.querySelector("[data-packery-sentinel]");
  const nextUrl =
    sentinel instanceof HTMLElement ? sentinel.dataset.nextUrl || "" : "";
  return { items, nextUrl };
}

/**
 * Lays out child items with Packery using unconstrained (pixel-granular) bin packing.
 *
 * Each item gets a random width within a configurable range, expressed as a
 * percentage of the container width. Heights stay natural, so cards keep their
 * image aspect ratio. Because no columnWidth/rowHeight is set, Packery packs
 * items wherever they fit instead of snapping them to a grid.
 *
 * Item widths are stored in sessionStorage so a reload during the same tab
 * session restores the same collage. New items are packed with Packery's
 * appended() into leftover space instead of re-packing cards that already
 * have positions. Scroll restoration for pages past the first 30 products is
 * documented above pageHref().
 *
 * Data attributes:
 * - data-packery-src: asset URL for packery.pkgd.min.js
 * - data-session-key: sessionStorage namespace (usually the collection handle)
 * - data-section-id: Shopify section id for loading the next page of items
 * - data-mobile-min-size/data-desktop-min-size: minimum item width, as % of container width
 * - data-mobile-max-size/data-desktop-max-size: maximum item width, as % of container width
 * - data-mobile-gutter/data-desktop-gutter: space between items in px
 */
class PackeryLayout extends HTMLElement {
  /** @type {any} */
  #packery;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {IntersectionObserver | undefined} */
  #loadMoreObserver;

  /** @type {number} */
  #lastWidth = 0;

  /** @type {boolean} */
  #ready = false;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #refreshQueued = false;

  /** @type {WeakSet<HTMLImageElement>} */
  #boundImages = new WeakSet();

  /** @type {WeakMap<HTMLElement, number>} */
  #itemHeights = new WeakMap();

  /** @type {string} */
  #nextUrl = "";

  /** @type {boolean} */
  #loadingMore = false;

  /** Products in the server-rendered page, captured before any append. */
  #initialItemCount = 0;

  /** True while a back/forward restore is fetching pages itself. */
  #autoLoadSuspended = false;

  /** @type {boolean} */
  #restoreApplied = false;

  /** @type {boolean} */
  #scrollRestoreCancelled = false;

  /** @type {boolean} */
  #applyingScroll = false;

  /** @type {number} */
  #lastScrollX = 0;

  /** @type {number} */
  #lastScrollY = 0;

  /** @type {number} */
  #savedScrollY = 0;

  /** @type {number} */
  #savedAt = 0;

  /** @type {boolean} */
  #scrollRestoreBound = false;

  /** Set in disconnectedCallback before in-flight restores observe it. */
  #disconnected = false;

  /** @type {Promise<void> | undefined} */
  #loadAllPromise;

  /** Smallest width an item may be assigned, so cards stay usable on narrow screens. */
  #minItemPx = 100;

  connectedCallback() {
    this.#disconnected = false;
    // Ensure the host participates in block layout even if section CSS is late.
    this.style.display = "block";
    this.style.width = "100%";

    const src = this.dataset.packerySrc;
    if (!src) {
      this.#markReady();
      return;
    }

    this.#nextUrl = this.#sentinel?.dataset.nextUrl || "";
    this.#abortController = new AbortController();
    this.#bindScrollRestore();
    const { signal } = this.#abortController;

    // Don't leave the grid invisible forever if something fails.
    const failsafe = setTimeout(() => this.#markReady(), 3000);
    signal.addEventListener("abort", () => clearTimeout(failsafe));

    Promise.all([loadPackery(src), whenHasWidth(this)])
      .then(([Packery]) => {
        if (signal.aborted) return;
        this.#init(Packery);
      })
      .catch((error) => {
        console.error(error);
        this.#markReady();
      });
  }

  disconnectedCallback() {
    this.#disconnected = true;
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#resizeObserver?.disconnect();
    this.#loadMoreObserver?.disconnect();
    this.#unbindScrollRestore();
    this.#packery?.destroy();
    this.#packery = undefined;
  }

  get #grid() {
    return /** @type {HTMLElement | null} */ (
      this.querySelector("[data-packery-grid]")
    );
  }

  get #sentinel() {
    return /** @type {HTMLElement | null} */ (
      this.querySelector("[data-packery-sentinel]")
    );
  }

  get #items() {
    return /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll("[data-packery-item]:not([hidden])"),
    ]);
  }

  get #sessionKey() {
    return this.dataset.sessionKey || "";
  }

  /**
   * @param {any} Packery
   */
  #init(Packery) {
    const grid = this.#grid;
    if (!grid || this.#packery) return;

    this.#lastWidth = this.clientWidth;
    this.#applyItemSizes();

    this.#packery = new Packery(grid, {
      itemSelector: "[data-packery-item]:not([hidden])",
      gutter: this.#gutter,
      transitionDuration: "0.3s",
      hiddenStyle: { opacity: 0 },
      visibleStyle: { opacity: 1 },
      // We handle resizing ourselves so widths can be recomputed first
      resize: false,
    });

    this.#packery.once("layoutComplete", () => this.#markReady());
    this.#packery.layout();
    this.#recordItemHeights();

    // Relayout when the container *width* changes. Height changes from
    // appending items must not re-pack cards that already have positions.
    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this);

    this.#bindImageLoads();

    if (this.#refreshQueued) {
      this.#refreshQueued = false;
      this.refresh();
    }

    // Count the server-rendered page before the sentinel is allowed to append.
    this.#initialItemCount = this.querySelectorAll("[data-packery-item]").length;
    const saved = readScrollRestore();
    if (this.#shouldRestoreScroll(saved)) {
      this.#autoLoadSuspended = true;
      this.#observeLoadMore();
      void this.#restoreScroll(saved);
      return;
    }

    if (
      document.documentElement.getAttribute("data-packery-scroll-restore") ===
      SCROLL_RESTORE_PENDING
    ) {
      this.#clearScrollRestoreChrome();
      void this.#releaseScrollRestoration();
    }
    this.#observeLoadMore();
  }

  /**
   * Rebuilds the layout after items are shown, hidden, added, or removed.
   * Safe to call before Packery has finished loading.
   */
  refresh() {
    if (!this.#packery) {
      this.#refreshQueued = true;
      return;
    }

    this.#applyItemSizes();
    this.#bindImageLoads();
    this.#packery.reloadItems();
    this.#packery.layout();
    this.#recordItemHeights();
  }

  /**
   * Adds items at the end of the grid and packs them into leftover space.
   * Existing cards keep their positions as long as their sizes are unchanged.
   *
   * @param {Iterable<Element>} elements
   */
  appendItems(elements) {
    const grid = this.#grid;
    const items = [...elements].filter(
      (element) => element instanceof HTMLElement,
    );
    if (!grid || !items.length) return;

    const sentinel = this.#sentinel;
    for (const item of items) {
      item.style.position = "absolute";
      if (sentinel) grid.insertBefore(item, sentinel);
      else grid.appendChild(item);
    }

    this.#applyItemSizes(items);
    this.#bindImageLoads(items);

    if (!this.#packery) {
      this.#refreshQueued = true;
      this.#syncLoadMore();
      return;
    }

    this.#packery.appended(items);
    this.#recordItemHeights(items);
    this.dispatchEvent(
      new CustomEvent("packery:appended", {
        bubbles: true,
        detail: { items },
      }),
    );
    this.#syncLoadMore();
  }

  /**
   * Fetches every remaining page and appends the items. Search uses this so
   * filtered products are not missing because they have not scrolled into view.
   *
   * @returns {Promise<void>}
   */
  loadAll() {
    if (!this.#nextUrl) return this.#loadAllPromise ?? Promise.resolve();
    this.#loadAllPromise ??= this.#waitWhileLoadingMore().then(() =>
      this.#loadRemainingPages(),
    );
    return this.#loadAllPromise;
  }

  async #waitWhileLoadingMore() {
    while (this.#loadingMore) {
      await new Promise((resolve) => setTimeout(resolve, 40));
    }
  }

  #markReady() {
    if (this.#ready) return;
    this.#ready = true;
    this.#grid?.setAttribute("data-layout-ready", "");
  }

  /**
   * @param {Iterable<HTMLElement>} [scope]
   */
  #bindImageLoads(scope) {
    const root = scope ? [...scope] : [this];
    /** @type {HTMLImageElement[]} */
    const images = [];
    for (const node of root) {
      if (node instanceof HTMLImageElement) images.push(node);
      images.push(...node.querySelectorAll("img"));
    }

    for (const image of images) {
      if (image.complete || this.#boundImages.has(image)) continue;
      this.#boundImages.add(image);
      image.addEventListener(
        "load",
        () => {
          const item = image.closest("[data-packery-item]");
          if (!(item instanceof HTMLElement)) return;
          const height = item.offsetHeight;
          if (this.#itemHeights.get(item) === height) return;
          this.#itemHeights.set(item, height);
          this.#packery?.layout();
        },
        { once: true },
      );
    }
  }

  #handleResize = debounce(() => {
    if (!this.#packery) return;

    const width = this.clientWidth;
    // Ignore height-only resizes from appending items; those must not re-pack.
    if (width <= 0 || width === this.#lastWidth) return;

    this.#lastWidth = width;
    this.#applyItemSizes();
    this.#packery.option({ gutter: this.#gutter });
    this.#packery.layout();
    this.#recordItemHeights();
  }, 100);

  /**
   * Assigns each item a width from its random fraction of the container width.
   * The fraction is generated once per item and stored, so resizes scale the
   * layout instead of reshuffling it. sessionStorage restores those fractions
   * for the rest of the tab session.
   *
   * @param {HTMLElement[]} [items]
   */
  #applyItemSizes(items = this.#items) {
    const containerWidth = this.#grid?.clientWidth || this.clientWidth;
    if (!containerWidth) return;

    const session = readSessionFractions(this.#sessionKey);
    let sessionChanged = false;

    // Mobile is two columns. A percentage width ignores the gutter, which
    // leaves a strip of empty space along the right edge.
    if (this.#isMobile && this.#maxSize <= 50) {
      const width = (containerWidth - this.#gutter) / 2;
      for (const item of items) {
        sessionChanged = this.#restoreItemFractions(item, session) || sessionChanged;
        sessionChanged = this.#captureItemFractions(item, session) || sessionChanged;
        item.style.width = `${width}px`;
      }
      if (sessionChanged) writeSessionFractions(this.#sessionKey, session);
      return;
    }

    for (const item of items) {
      sessionChanged = this.#restoreItemFractions(item, session) || sessionChanged;

      const fractionKey = this.#isMobile
        ? "packeryMobileFraction"
        : "packeryDesktopFraction";
      let fraction = Number(item.dataset[fractionKey]);

      if (!fraction) {
        fraction = this.#randomFraction();
        item.dataset[fractionKey] = fraction.toFixed(4);
      }

      sessionChanged = this.#captureItemFractions(item, session) || sessionChanged;

      const width = Math.min(
        containerWidth,
        Math.max(this.#minItemPx, Math.round(containerWidth * fraction)),
      );
      item.style.width = `${width}px`;
    }

    if (sessionChanged) writeSessionFractions(this.#sessionKey, session);
  }

  /**
   * @param {HTMLElement} item
   * @param {Record<string, Record<string, string>>} session
   * @returns {boolean}
   */
  #restoreItemFractions(item, session) {
    const id = itemSessionId(item);
    const saved = id ? session[id] : undefined;
    if (!saved) return false;

    let changed = false;
    for (const key of FRACTION_KEYS) {
      if (item.dataset[key] || !saved[key]) continue;
      item.dataset[key] = saved[key];
      changed = true;
    }
    return changed;
  }

  /**
   * @param {HTMLElement} item
   * @param {Record<string, Record<string, string>>} session
   * @returns {boolean}
   */
  #captureItemFractions(item, session) {
    const id = itemSessionId(item);
    if (!id) return false;

    /** @type {Record<string, string>} */
    const next = { ...(session[id] || {}) };
    let changed = false;
    for (const key of FRACTION_KEYS) {
      const value = item.dataset[key];
      if (!value || next[key] === value) continue;
      next[key] = value;
      changed = true;
    }
    if (!changed) return false;
    session[id] = next;
    return true;
  }

  /**
   * @param {HTMLElement[]} [items]
   */
  #recordItemHeights(items = this.#items) {
    for (const item of items) this.#itemHeights.set(item, item.offsetHeight);
  }

  /**
   * Random fraction between min and max size, biased slightly toward smaller
   * items (like the Packery site hero) so gaps fill in more easily.
   */
  #randomFraction() {
    const min = this.#minSize / 100;
    const max = this.#maxSize / 100;
    const bias = Math.random() * Math.random();
    return min + bias * (max - min);
  }

  #observeLoadMore() {
    this.#loadMoreObserver?.disconnect();
    if (this.#autoLoadSuspended) return;

    const sentinel = this.#sentinel;
    if (!sentinel || !this.#nextUrl) return;

    this.#loadMoreObserver = new IntersectionObserver(
      (entries) => {
        if (this.#autoLoadSuspended) return;
        if (entries.some((entry) => entry.isIntersecting)) this.#loadNextPage();
      },
      { rootMargin: LOAD_MORE_ROOT_MARGIN },
    );
    this.#loadMoreObserver.observe(sentinel);
  }

  #syncLoadMore() {
    const sentinel = this.#sentinel;
    if (!sentinel) {
      this.#nextUrl = "";
      this.#loadMoreObserver?.disconnect();
      return;
    }

    if (this.#nextUrl) {
      sentinel.dataset.nextUrl = this.#nextUrl;
      sentinel.hidden = false;
      this.#observeLoadMore();
      return;
    }

    sentinel.remove();
    this.#loadMoreObserver?.disconnect();
  }

  async #loadRemainingPages() {
    /** @type {HTMLElement[]} */
    const collected = [];
    try {
      while (this.#nextUrl && !this.#abortController?.signal.aborted) {
        const page = await this.#fetchNextItems();
        collected.push(...page.items);
        this.#nextUrl = page.nextUrl;
        if (!page.items.length && !page.nextUrl) break;
      }
      if (collected.length) this.appendItems(collected);
    } catch (error) {
      this.#loadAllPromise = undefined;
      console.error(error);
    } finally {
      this.#syncLoadMore();
    }
  }

  async #loadNextPage() {
    if (this.#loadingMore || this.#loadAllPromise || !this.#nextUrl) return;

    this.#loadingMore = true;
    this.setAttribute("aria-busy", "true");
    try {
      const page = await this.#fetchNextItems();
      this.#nextUrl = page.nextUrl;
      if (page.items.length) this.appendItems(page.items);
      else this.#syncLoadMore();
    } catch (error) {
      console.error(error);
    } finally {
      this.#loadingMore = false;
      this.removeAttribute("aria-busy");
    }
  }

  /**
   * @returns {Promise<{ items: HTMLElement[], nextUrl: string }>}
   */
  async #fetchNextItems() {
    const nextUrl = this.#nextUrl;
    if (!nextUrl) return { items: [], nextUrl: "" };

    const html = await this.#fetchPageHtml(nextUrl);
    const page = parsePackeryPage(html);
    const existing = new Set(
      [...this.querySelectorAll("[data-packery-key]")]
        .map((item) =>
          item instanceof HTMLElement ? item.dataset.packeryKey : "",
        )
        .filter(Boolean),
    );
    const items = page.items.filter((item) => {
      const id = itemSessionId(item);
      return !id || !existing.has(id);
    });
    let nextUrlAfter = page.nextUrl;
    if (!items.length && nextUrlAfter === nextUrl) nextUrlAfter = "";
    return { items, nextUrl: nextUrlAfter };
  }

  /**
   * @param {string} nextUrl
   * @returns {Promise<string>}
   */
  async #fetchPageHtml(nextUrl) {
    const url = new URL(nextUrl, window.location.href);
    const sectionId = this.dataset.sectionId;
    if (sectionId) url.searchParams.set("section_id", sectionId);

    const response = await fetch(url.toString(), {
      signal: this.#abortController?.signal,
    });
    if (!response.ok) {
      throw new Error(`Failed to load more products (${response.status})`);
    }

    const type = response.headers.get("content-type") || "";
    if (type.includes("application/json")) {
      const json = await response.json();
      if (sectionId && typeof json?.[sectionId] === "string") {
        return json[sectionId];
      }
      const first = Object.values(json || {}).find(
        (value) => typeof value === "string",
      );
      return typeof first === "string" ? first : "";
    }

    return response.text();
  }

  #bindScrollRestore() {
    if (this.#scrollRestoreBound) return;
    this.#scrollRestoreBound = true;
    this.#lastScrollX = window.scrollX;
    this.#lastScrollY = window.scrollY;
    window.addEventListener("pagehide", this.#onPageHide);
    window.addEventListener("pageshow", this.#onPageShow);
    window.addEventListener("scroll", this.#onScroll, { passive: true });
    this.addEventListener("click", this.#onClickCapture, true);
  }

  #unbindScrollRestore() {
    if (!this.#scrollRestoreBound) return;
    this.#scrollRestoreBound = false;
    window.removeEventListener("pagehide", this.#onPageHide);
    window.removeEventListener("pageshow", this.#onPageShow);
    window.removeEventListener("scroll", this.#onScroll);
    this.removeEventListener("click", this.#onClickCapture, true);
    this.#removeRestoreIntentListeners();
  }

  #onPageHide = () => {
    this.#saveScrollRestore();
  };

  /**
   * Back/forward cache restore. The products are already in the DOM, so this
   * only re-applies the offset when the browser did not.
   *
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;
    const saved = readScrollRestore();
    if (!this.#scrollRestoreRecordMatches(saved)) return;
    const count = this.querySelectorAll("[data-packery-item]").length;
    if (count < (Number(saved.itemCount) || 0)) return;
    const y = Number(saved.scrollY) || 0;
    const x = Number(saved.scrollX) || 0;
    if (Math.abs(window.scrollY - y) > 2 || Math.abs(window.scrollX - x) > 2) {
      this.#scrollToSaved(saved);
    }
  };

  #onScroll = () => {
    if (this.#applyingScroll) return;
    this.#lastScrollX = window.scrollX;
    this.#lastScrollY = window.scrollY;
  };

  #onClickCapture = () => {
    this.#saveScrollRestore();
  };

  #onRestoreIntent = () => {
    this.#scrollRestoreCancelled = true;
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onRestoreKeydown = (event) => {
    if (
      event.key === "ArrowDown" ||
      event.key === "ArrowUp" ||
      event.key === "PageDown" ||
      event.key === "PageUp" ||
      event.key === "Home" ||
      event.key === "End" ||
      event.key === " "
    ) {
      this.#scrollRestoreCancelled = true;
    }
  };

  #addRestoreIntentListeners() {
    window.addEventListener("wheel", this.#onRestoreIntent, { passive: true });
    window.addEventListener("touchmove", this.#onRestoreIntent, { passive: true });
    window.addEventListener("keydown", this.#onRestoreKeydown);
  }

  #removeRestoreIntentListeners() {
    window.removeEventListener("wheel", this.#onRestoreIntent);
    window.removeEventListener("touchmove", this.#onRestoreIntent);
    window.removeEventListener("keydown", this.#onRestoreKeydown);
  }

  /**
   * Some browsers zero window.scrollY before pagehide when a link is followed.
   * The click that started that navigation already stored the real offset.
   *
   * @returns {number}
   */
  #scrollYForSave() {
    const live = window.scrollY;
    if (
      live < 1 &&
      this.#savedScrollY > 1 &&
      Date.now() - this.#savedAt < 1500
    ) {
      return this.#savedScrollY;
    }
    if (live < 1 && this.#lastScrollY > 1) return this.#lastScrollY;
    return live;
  }

  /**
   * @param {HTMLElement[]} [items]
   * @returns {number}
   */
  #measureInitialScrollHeight(items) {
    const all = items || [...this.querySelectorAll("[data-packery-item]")];
    if (!this.#initialItemCount || all.length === 0) {
      return document.documentElement.scrollHeight;
    }
    const bottoms = all.map(
      (item) => item.getBoundingClientRect().bottom + window.scrollY,
    );
    return estimateInitialScrollHeight({
      initialBottoms: bottoms.slice(0, this.#initialItemCount),
      allBottoms: bottoms,
      scrollHeight: document.documentElement.scrollHeight,
    });
  }

  /**
   * Writes the ScrollRestoreState record for this URL. See the note above pageHref().
   */
  #saveScrollRestore() {
    const root = document.documentElement;
    const pending =
      root.getAttribute("data-packery-scroll-restore") === SCROLL_RESTORE_PENDING;
    if (pending && !this.#restoreApplied && !this.#scrollRestoreCancelled) return;

    const items = [...this.querySelectorAll("[data-packery-item]")];
    const loadedExtra =
      this.#initialItemCount > 0 && items.length > this.#initialItemCount;
    const scrollY = this.#scrollYForSave();
    const needsRestore = needsPackeryScrollRestore({
      scrollY,
      viewportHeight: window.innerHeight,
      initialScrollHeight: this.#measureInitialScrollHeight(items),
      loadedExtra,
    });
    const scrollX = window.scrollX || this.#lastScrollX || 0;

    this.#savedScrollY = scrollY;
    this.#savedAt = Date.now();
    writeScrollRestore({
      href: pageHref(),
      sessionKey: this.#sessionKey,
      scrollX,
      scrollY,
      itemCount: items.length,
      needsRestore,
    });
  }

  /**
   * @param {Record<string, any> | null} saved
   * @returns {boolean}
   */
  #scrollRestoreRecordMatches(saved) {
    if (!saved?.needsRestore) return false;
    if (saved.href !== pageHref()) return false;
    if (saved.sessionKey && saved.sessionKey !== this.#sessionKey) return false;
    return true;
  }

  /**
   * @param {Record<string, any> | null} saved
   * @returns {boolean}
   */
  #shouldRestoreScroll(saved) {
    if (!this.#scrollRestoreRecordMatches(saved)) return false;
    const pending =
      document.documentElement.getAttribute("data-packery-scroll-restore") ===
      SCROLL_RESTORE_PENDING;
    return pending || isBackForwardNavigation();
  }

  /**
   * Fetches the pages that were on screen, then applies the saved offset.
   * See the note above pageHref().
   *
   * @param {Record<string, any>} saved
   */
  async #restoreScroll(saved) {
    this.#scrollRestoreCancelled = false;
    this.#addRestoreIntentListeners();
    try {
      await this.#loadUntilCount(Number(saved.itemCount) || 0);
    } catch (error) {
      if (/** @type {any} */ (error)?.name !== "AbortError") console.error(error);
    } finally {
      const aborted =
        this.#disconnected || Boolean(this.#abortController?.signal.aborted);
      this.#autoLoadSuspended = false;
      this.#removeRestoreIntentListeners();
      if (!aborted && !this.#scrollRestoreCancelled) {
        await new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
        // Drop the spacer before scrolling so the offset lands in the real
        // document, which is now tall enough to hold it.
        this.#clearScrollRestoreSpacer();
        this.#scrollToSaved(saved);
        this.#restoreApplied = true;
        this.#scrollToSaved(saved);
      } else {
        this.#clearScrollRestoreSpacer();
      }
      this.#revealRestoredGrid();
      if (!aborted) {
        this.#observeLoadMore();
        await this.#releaseScrollRestoration();
      }
    }
  }

  /**
   * @param {number} target
   */
  async #loadUntilCount(target) {
    while (!this.#abortController?.signal.aborted && !this.#scrollRestoreCancelled) {
      if (this.#loadAllPromise) {
        await this.#loadAllPromise;
        break;
      }
      const count = this.querySelectorAll("[data-packery-item]").length;
      if (target > 0 && count >= target) break;
      if (!this.#nextUrl) break;
      const before = count;
      await this.#loadNextPage();
      const after = this.querySelectorAll("[data-packery-item]").length;
      if (after <= before) {
        if (this.#loadAllPromise) await this.#loadAllPromise;
        break;
      }
    }
  }

  /**
   * @param {Record<string, any>} saved
   */
  #scrollToSaved(saved) {
    const x = Number(saved.scrollX) || 0;
    const y = Number(saved.scrollY) || 0;
    this.#applyingScroll = true;
    window.scrollTo(x, y);
    this.#lastScrollX = window.scrollX;
    this.#lastScrollY = window.scrollY;
    this.#applyingScroll = false;
  }

  #clearScrollRestoreSpacer() {
    const root = document.documentElement;
    root.style.minHeight = "";
    root.style.overflowAnchor = "";
  }

  #revealRestoredGrid() {
    const root = document.documentElement;
    root.removeAttribute("data-packery-scroll-restore");
    root.removeAttribute("data-packery-scroll-x");
    root.removeAttribute("data-packery-scroll-y");
  }

  #clearScrollRestoreChrome() {
    this.#clearScrollRestoreSpacer();
    this.#revealRestoredGrid();
  }

  async #releaseScrollRestoration() {
    const root = document.documentElement;
    const previous = root.getAttribute("data-packery-scroll-restoration");
    root.removeAttribute("data-packery-scroll-restoration");
    if (previous !== "manual" && previous !== "auto") return;
    if (!("scrollRestoration" in history)) return;

    if (document.readyState !== "complete") {
      await new Promise((resolve) => {
        window.addEventListener("load", resolve, { once: true });
      });
    }
    // Let the load event finish while restoration is still manual, so the
    // browser does not clamp the offset after the pages have been filled in.
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (this.#disconnected || this.#abortController?.signal.aborted) return;
    if (history.scrollRestoration === "manual") {
      history.scrollRestoration = previous;
    }
  }

  get #isMobile() {
    return window.matchMedia("(max-width: 767px)").matches;
  }

  get #minSize() {
    const value = Number(
      this.dataset[this.#isMobile ? "mobileMinSize" : "desktopMinSize"],
    );
    return Number.isFinite(value) && value > 0 ? value : 18;
  }

  get #maxSize() {
    const value = Number(
      this.dataset[this.#isMobile ? "mobileMaxSize" : "desktopMaxSize"],
    );
    return Number.isFinite(value) && value >= this.#minSize
      ? value
      : Math.max(this.#minSize, 38);
  }

  get #gutter() {
    const value = Number(
      this.dataset[this.#isMobile ? "mobileGutter" : "desktopGutter"],
    );
    return Number.isFinite(value) && value >= 0 ? value : 8;
  }
}

if (typeof window !== "undefined") {
  installEarlyPackeryScrollRestore();
}

if (typeof customElements !== "undefined" && !customElements.get("packery-layout")) {
  customElements.define("packery-layout", PackeryLayout);
}

if (typeof module !== "undefined" && module.exports) {
  module.exports = {
    sessionStorageKey,
    readSessionFractions,
    writeSessionFractions,
    parsePackeryPage,
    itemSessionId,
    estimateInitialScrollHeight,
    needsPackeryScrollRestore,
    SCROLL_RESTORE_KEY,
    LOAD_MORE_ROOT_MARGIN,
  };
}
